import React, { useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';

const DISCONTINUE_REASONS = [
  { value: 'wound_healed', label: 'Wound Healed' },
  { value: 'change_in_care', label: 'Change in Care' },
  { value: 'patient_refusal', label: 'Patient Refusal' },
  { value: 'other', label: 'Other' },
];

interface HealWoundPopoverProps {
  minDate: Date;
  triggerLabel?: string;
  onDiscontinue: (
    date: Date,
    reason: string,
    comments: string
  ) => Promise<void>;
}

const HealWoundPopover: React.FC<HealWoundPopoverProps> = ({
  minDate,
  triggerLabel = 'Discontinue',
  onDiscontinue,
}) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [otherText, setOtherText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setDate(undefined);
    setReason('');
    setOtherText('');
  };

  const handleDiscontinue = async () => {
    if (!date || !reason) return;
    setIsSubmitting(true);
    try {
      const comments = reason === 'other' ? otherText.trim() : '';
      await onDiscontinue(date, reason, comments);
      setOpen(false);
      reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid =
    !!date && !!reason && (reason !== 'other' || otherText.trim().length > 0);

  return (
    <>
      <Button
        variant='outline'
        size='sm'
        title='Discontinue wound'
        onClick={() => setOpen(true)}
      >
        <CheckCircle className='h-4 w-4 mr-1' />
        {triggerLabel}
      </Button>

      <Dialog
        open={open}
        onOpenChange={nextOpen => {
          setOpen(nextOpen);
          if (!nextOpen) reset();
        }}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Discontinue Wound</DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='space-y-1'>
              <Label className='text-xs'>Date Discontinued</Label>
              <DatePicker
                date={date}
                onDateChange={d => {
                  setDate(d);
                  if (!d) {
                    setReason('');
                    setOtherText('');
                  }
                }}
                placeholder='Select date'
                datePickerSize='default'
                minDate={minDate}
                showClearButton
              />
            </div>
            {date && (
              <>
                <div className='space-y-1'>
                  <Label className='text-xs'>Reason</Label>
                  <Select
                    value={reason}
                    onValueChange={v => {
                      setReason(v);
                      if (v !== 'other') setOtherText('');
                    }}
                  >
                    <SelectTrigger selectSize='sm'>
                      <SelectValue placeholder='Select reason...' />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCONTINUE_REASONS.map(r => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {reason === 'other' && (
                  <div className='space-y-1'>
                    <Label className='text-xs'>Please specify</Label>
                    <Input
                      placeholder='Describe reason...'
                      value={otherText}
                      onChange={e => setOtherText(e.target.value)}
                      inputSize='sm'
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              size='sm'
              onClick={() => {
                setOpen(false);
                reset();
              }}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              onClick={handleDiscontinue}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting && (
                <Loader2 className='h-4 w-4 mr-1 animate-spin' />
              )}
              Discontinue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HealWoundPopover;
